import React, { useCallback, useMemo } from 'react';
import { memberId } from '@trello/session-cookie';
import styles from './BoardClosed.less';
import { useReopenBoardMutation } from './ReopenBoardMutation.generated';
import { LazyWorkspaceChooser } from 'app/src/components/WorkspaceChooser';
import { Analytics } from '@trello/atlassian-analytics';
import { Spinner } from '@trello/nachos/spinner';
import { useBoardPermissionsAndDetailsQuery } from './BoardPermissionsAndDetailsQuery.generated';
import { useCanDeleteBoard } from './useCanDeleteBoard';
import { forNamespace, localizeCount } from '@trello/i18n';
import { MemberAvatar } from 'app/src/components/MemberAvatar';
import { DeleteBoardButton } from 'app/src/components/DeleteBoardButton';
import { AdminUpsellBoardClosed } from './AdminUpsellBoardClosed';
import { ProductFeatures } from '@trello/product-features';
import classNames from 'classnames';
import { CloseBoardTestIds } from '@trello/test-ids';

interface BoardClosedProps {
  boardId: string;
  isAtOrOverLimit: boolean;
}

const f = forNamespace('closed board view', {
  shouldEscapeStrings: false,
});

export const BoardClosed: React.FC<BoardClosedProps> = ({
  boardId,
  isAtOrOverLimit,
}) => {
  const { data, loading } = useBoardPermissionsAndDetailsQuery({
    variables: { boardId },
  });

  const { loading: canDeleteLoading, result: canDelete } = useCanDeleteBoard(
    boardId,
  );

  const [reopenBoard] = useReopenBoardMutation();

  const onWorkspaceChosen = useCallback(
    async (orgId) => {
      const keepBillableGuests = Boolean(
        data?.board?.organization?.memberships?.find(
          (membership) =>
            membership.idMember === memberId &&
            membership.memberType === 'admin',
        ),
      );

      try {
        await reopenBoard({
          variables: { boardId, keepBillableGuests },
        });

        Analytics.sendTrackEvent({
          action: 'reopened',
          actionSubject: 'board',
          source: 'closedBoardScreen',
          containers: {
            board: {
              id: boardId,
            },
            organization: {
              id: orgId,
            },
          },
        });

        window.location.reload();
      } catch {
        // To Do: error state
      }
    },
    [reopenBoard, boardId, data?.board?.organization?.memberships],
  );

  const boardName = data?.board?.name || '';
  const workspaceId = data?.board?.idOrganization || '';
  const meMembership = data?.board?.memberships?.find(
    (membership) => membership.idMember === memberId,
  );
  const isBoardAdmin = meMembership?.memberType === 'admin';
  const isPaidWorkspace = useMemo(
    () => ProductFeatures.hasProduct(data?.board?.organization?.products?.[0]),
    [data?.board?.organization?.products],
  );
  const isWorkspaceAdmin = useMemo(
    () =>
      data?.board?.organization?.memberships?.some(
        (member) =>
          member.idMember === memberId && member.memberType === 'admin',
      ),
    [data?.board?.organization?.memberships],
  );
  const canReopen = isBoardAdmin || (isPaidWorkspace && isWorkspaceAdmin);

  const adminIdArray = data?.board?.memberships
    ?.filter((member) => member.memberType === 'admin')
    .map((member) => member.idMember);
  const adminData = data?.board?.members?.filter((member) => {
    return adminIdArray?.includes(member.id);
  });
  const adminCount = adminData?.length || 0;
  const shouldShowBoardLimitView = isAtOrOverLimit && canReopen;

  const isNormalBoardMember = meMembership?.memberType === 'normal';
  const isBoardObserver = meMembership?.memberType === 'observer';
  const shouldSeeBoardAdmins =
    isNormalBoardMember ||
    isBoardObserver ||
    (!isPaidWorkspace &&
      isWorkspaceAdmin &&
      data?.board?.prefs?.permissionLevel === 'org');

  return (
    <div className={styles.container}>
      <div className={styles.closedModalContainer}>
        <div
          className={classNames(styles.closedModal, {
            [styles.upsellModal]: shouldShowBoardLimitView,
          })}
        >
          {loading || canDeleteLoading ? (
            <Spinner centered />
          ) : shouldShowBoardLimitView ? (
            <AdminUpsellBoardClosed
              orgId={workspaceId}
              boardId={boardId}
              boardName={boardName}
            />
          ) : (
            <>
              <h1 data-test-id={CloseBoardTestIds.CloseBoardBigMessage}>
                {f('board closed title', { boardName })}
              </h1>
              {canReopen || canDelete ? (
                <>
                  {canReopen && (
                    <div className={styles.reopenButton}>
                      <LazyWorkspaceChooser
                        boardId={boardId}
                        triggerButtonText={f('reopen cta')}
                        confirmButtonText={f('reopen cta')}
                        shouldFitContainer={false}
                        onSuccess={onWorkspaceChosen}
                      />
                    </div>
                  )}
                  {canDelete && (
                    <div className={styles.deleteLink}>
                      <DeleteBoardButton
                        source="boardScreen"
                        boardId={boardId}
                      />
                    </div>
                  )}
                </>
              ) : (
                shouldSeeBoardAdmins && (
                  <>
                    <p className={styles.adminText}>
                      {localizeCount('contact board admins', adminCount)}
                    </p>
                    <div className={styles.adminSection}>
                      {adminData?.map((member) => (
                        <div key={member.id} className={styles.adminNames}>
                          <div className={styles.avatar}>
                            <MemberAvatar idMember={member.id} size={24} />
                          </div>
                          <div>{member.fullName}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
